import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StatCard, Tag, statusTone, Loader, Empty } from "@/components/kit";
import {
  Briefcase, CalendarCog, Store, Users, Wallet, ShieldCheck, ArrowRight,
  Trophy, FileText, MapPin,
} from "lucide-react";

export default function OrgDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/org/dashboard").then((r) => setData(r.data)).catch(() => setData(false)); }, []);

  if (data === null) return <Loader label="Loading organisation workspace" />;
  if (!data) return <Empty label="Could not load dashboard" />;

  const c = data.counts;
  const inr = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

  return (
    <div className="fade-up">
      <PageHeader overline="Organisation workspace" title={data.org.name}
        subtitle={`${data.org.type} · ${data.org.location.city}, ${data.org.location.state}`}
        actions={data.org.verified && <Tag tone="emerald"><ShieldCheck className="w-3 h-3" /> Krida Verified</Tag>}
        testid="org-dashboard" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Open opportunities" value={c.open_opportunities} icon={Briefcase} accent="cobalt" testid="stat-open-opps" />
        <StatCard label="Tournaments" value={c.tournaments} icon={Trophy} accent="amber" />
        <StatCard label="Confirmed workforce" value={c.confirmed_workforce} icon={Users} accent="emerald" />
        <StatCard label="Payments due" value={inr(c.payments_due)} icon={Wallet} accent="crimson" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="krida-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold flex items-center gap-2"><CalendarCog className="w-5 h-5 text-krida-amber" /> Tournaments & events</h3>
              <Link to="/app/ops" className="text-sm text-krida-crimson flex items-center gap-1">Krida Ops <ArrowRight className="w-3.5 h-3.5" /></Link>
            </div>
            <div className="space-y-3">
              {data.tournaments.length === 0 && <Empty label="No tournaments yet" />}
              {data.tournaments.map((t) => {
                const req = t.requirements.reduce((s, r) => s + r.required, 0);
                const conf = t.requirements.reduce((s, r) => s + r.confirmed, 0);
                return (
                  <Link to={`/app/ops/${t.id}`} key={t.id} className="block p-4 rounded-xl bg-black/20 border border-white/5 hover:border-white/15 transition-colors" data-testid={`org-tournament-${t.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-semibold">{t.name}</div>
                      <Tag tone={statusTone(t.status)}>{t.status}</Tag>
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-2 mt-1"><MapPin className="w-3 h-3" /> {t.venue} · {new Date(t.start_date).toLocaleDateString()}</div>
                    <div className="mt-3 flex items-center gap-2 text-xs">
                      <span className="font-mono text-krida-emerald">{conf}</span><span className="text-slate-500">/ {req} workforce confirmed</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="krida-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold flex items-center gap-2"><Briefcase className="w-5 h-5 text-krida-cobalt" /> Open opportunities</h3>
              <Link to="/app/work" className="text-sm text-krida-crimson flex items-center gap-1">Krida Work <ArrowRight className="w-3.5 h-3.5" /></Link>
            </div>
            <div className="space-y-3">
              {data.opportunities.length === 0 && <Empty label="No opportunities posted" />}
              {data.opportunities.map((o) => (
                <div key={o.id} className="flex items-center justify-between p-3 rounded-xl bg-black/20 border border-white/5">
                  <div><div className="font-semibold">{o.title}</div><div className="text-xs text-slate-500">{o.location.city} · {o.num_required} required</div></div>
                  <Tag tone="cobalt">{o.opportunity_type}</Tag>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="krida-card p-6">
            <h3 className="font-heading text-lg font-bold flex items-center gap-2 mb-4"><FileText className="w-4 h-4 text-krida-emerald" /> Recent applications</h3>
            {data.recent_applications.length === 0 && <div className="text-sm text-slate-500">No applications yet.</div>}
            <div className="space-y-3">
              {data.recent_applications.map((a) => (
                <div key={a.id} className="text-sm flex items-center justify-between">
                  <div className="min-w-0"><div className="font-medium truncate">{a.professional_name}</div><div className="text-xs text-slate-500 truncate">{a.opportunity_title}</div></div>
                  <Tag tone={statusTone(a.status)}>{a.status}</Tag>
                </div>
              ))}
            </div>
          </div>

          <div className="krida-card p-6">
            <h3 className="font-heading text-lg font-bold flex items-center gap-2 mb-4"><Store className="w-4 h-4 text-krida-crimson" /> Marketplace bookings</h3>
            {data.bookings.length === 0 && <div className="text-sm text-slate-500">No bookings yet.</div>}
            <div className="space-y-3">
              {data.bookings.map((b) => (
                <div key={b.id} className="text-sm flex items-center justify-between">
                  <div className="min-w-0"><div className="font-medium truncate">{b.coach_name}</div><div className="text-xs text-slate-500 truncate">{b.service}</div></div>
                  <Tag tone={statusTone(b.status)}>{b.status}</Tag>
                </div>
              ))}
            </div>
            <Link to="/app/marketplace" className="mt-4 w-full py-2.5 rounded-xl bg-krida-crimson/10 text-krida-crimson font-semibold text-sm flex items-center justify-center gap-2 hover:bg-krida-crimson/20 transition-colors">
              Find workforce <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
